import { PropsWithChildren, createContext, useCallback, useState } from 'react';
import { SchemasInformation } from 'models/general';
import { CurrentUserData } from 'models/user';
import { useLocalStorage } from '@mantine/hooks';
import { useUserData } from 'hooks/api/useUser';

interface AppContextType {
  currentUser?: CurrentUserData;
  isLoading: boolean;
  schemas?: SchemasInformation;
  selectedTableRowIds: string[];
  updateTableRowIds?: (newValue: string | string[]) => void;
  visibleGroupsIds?: string[];
  isNavBarCollapsed: boolean;
  setIsNavBarCollapsed: (collapsed: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);
AppContext.displayName = 'App';

const AppProvider = ({ children }: PropsWithChildren) => {
  const { data: currentUser, isLoading } = useUserData();
  const [selectedTableRowIds, setSelectedTableRowIds] = useState<string[]>([]);
  const [isNavBarCollapsed, setIsNavBarCollapsed] = useLocalStorage({
    key: 'isNavBarCollapsed',
    defaultValue: false,
  });

  const updateTableRowIds = useCallback((newValue: string | string[]) => {
    if (!Array.isArray(newValue)) {
      setSelectedTableRowIds((prev) =>
        prev.includes(newValue) ? prev.filter((id) => id !== newValue) : [...prev, newValue],
      );
    } else {
      setSelectedTableRowIds(newValue);
    }
  }, []);

  const visibleGroupsIds = currentUser?.visibleGroupsIds;

  return (
    <AppContext.Provider
      value={{
        currentUser,
        isLoading,
        selectedTableRowIds,
        updateTableRowIds,
        visibleGroupsIds,
        isNavBarCollapsed,
        setIsNavBarCollapsed,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export { AppContext, AppProvider };
